import "animate.css/animate.min.css";
// import "antd/dist/antd.min.css";
import { GoogleOAuthProvider } from "@react-oauth/google";
import Axios from "axios";
import React from "react";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import { setInterval, setTimeout } from "worker-timers";
import KuikaAuthApp from "./app-types/kuika-auth-app";
import { isMfeHandoffToken, readUsernameFromJwt } from "./auth/jwt-utils";
import LocalStorageService from "./auth/local-storage-service";
import { TwinChatWidget } from "./components/web-components/twin-dashboard/twin-chat-widget";
import { EExternalTokenProviderTypes } from "./models/dto/login.dto";
import { IRouterType } from "./routes/router";
import { ApplicationService } from "./services/application-service";
import HelperService from "./services/helper-service";
import { UserService } from "./services/user-service";
import { KuikaAppManager } from "./shared/utilty/kuika-app-manager";
import { KMainFunctions } from "./shared/utilty/main-functions";
import "./styles/chinese-ruleset.scss";
import "./styles/components/badge.scss";
import "./styles/components/divider.scss";
import "./styles/components/kanban-board.scss";
import "./styles/fonts.scss";
import "./styles/form.scss";
import "./styles/general.scss";
import "./styles/registration.scss";

import SessionService from "./services/session-service";
import { translationService } from './services/_singletons';
import { Modal, Button } from "antd/es";

// wasm-import-placeholder

export type IAuthType = "MSAL" | "Kuika";
export type IAppMode = "WebComponent" | "WebSite";

export interface IBootStrapperProps {
  authType: IAuthType;
  routerType?: IRouterType;
  tagName?: string;
  external_access_token?: string;
  external_refresh_token?: string;
  external_user?: string;
  startingScreen?: string;
  appMode?: IAppMode;
  // Allow only screenInput_* props with specific typing
  [key: `screenInput_${string}`]: unknown;
}

declare let window: any;

export interface IBootstrapperState {
  // session-service-state-placeholder
  isMounted: boolean;
  // True until the MFE auth handoff (or legacy localStorage bootstrap) finishes.
  // Login-state cookies are now scoped per app by name (is_logged_in__<apiHost>),
  // so PrivateRoute no longer mistakes the host's login for ours. This guard still
  // gates routes while an MFE handoff JWT (or a legacy localStorage token) is being
  // redeemed: our own backend has no session yet, so rendering screens early would
  // produce the 401 → refresh 500 → /login bounce.
  mfeHandoffPending: boolean;
  remainingSessionTime: number;
}

export class AppBootstrapper extends React.Component<IBootStrapperProps, IBootstrapperState> {
  
  private readonly inactivityThreshold = 10800;
  private readonly logoutWarningThreshold = 20;
  private readonly sessionService = new SessionService({
    bootstrapper: this,
    inactivityThresholdAsSeconds: this.inactivityThreshold,
    logoutWarningThreshold: this.logoutWarningThreshold,
    persistSessionAcrossPageReloads: false
  });

  private tokenCheckInterval: any = null;

  private isCheckingToken: boolean = false;

  constructor(props: IBootStrapperProps) {
    super(props);
    this.state = {
      isMounted: false,
      mfeHandoffPending: AppBootstrapper.computeMfeHandoffPending(props),
      // Initial value is set to 99999999999 to prevent the session warning modal from showing up on the first render.
      remainingSessionTime: 99999999999
    };

    const clientSecret = HelperService.getClientSecret();
    const clientId = HelperService.getClientId();

    Axios.defaults.headers.common.ClientSecret = clientSecret;
    Axios.defaults.headers.common["X-ClientId"] = clientId;
  }

  // A handoff is needed when this app is embedded as an MFE AND something
  // outside our own backend already authenticated the user — either an MFE
  // handoff JWT injected by a new-auth host, or a legacy localStorage token
  // left behind by a pre-cookie-auth host. In both cases, our own backend has
  // no session yet and we must redeem before letting routes render.
  private static computeMfeHandoffPending(props: IBootStrapperProps): boolean {
    if (props.appMode !== "WebComponent") return false;
    if (typeof window === "undefined") return false;

    if (props.external_access_token && isMfeHandoffToken(props.external_access_token)) {
      return true;
    }

    const alreadyLoggedInOnOurBackend = KuikaAppManager.readLoginStateCookie("is_logged_in") === "1";
    if (alreadyLoggedInOnOurBackend) return false;

    const hasLegacyToken = !!localStorage.getItem("tokenString");
    return hasLegacyToken;
  }

  getProps = () => {
    if (this.props == undefined) return "";
    return JSON.stringify(this.props);
  };

  checkIsTokenExpired = async () => {
    if (this.isCheckingToken) {
      return;
    }

    this.isCheckingToken = true;

    try {
      if (KuikaAppManager.readLoginStateCookie("is_logged_in") !== "1") {
        return;
      }

      if (location.href.includes("/resetpassword")) {
        return;
      }

      const isSessionValid: boolean = await UserService.GetIsTokenValid();

      if (isSessionValid === false) {
        if (localStorage.getItem("ml") === "tr_TR") {
          KMainFunctions.displayErrorNotification(
            "Oturum süresi doldu veya başka bir tarayıcıdan giriş yapıldı. Giriş sayfasına yönlendiriliyorsunuz.",
            {
              duration: 15000
            }
          );
        } else {
          KMainFunctions.displayErrorNotification(
            "Session expired or user logged in from another tab/browser. Redirecting to login page in 5 seconds",
            {
              duration: 15000
            }
          );
        }

        setTimeout(() => {
          LocalStorageService.clearToken();
          HelperService.gotoLogin();
        }, 5000);
      }
    } catch (error) {
      console.error("Error checking token expiration:", error);
    } finally {
      this.isCheckingToken = false;
    }
  };

  private startTokenExpirationCheck = () => {
    this.clearTokenExpirationCheck();

    this.checkIsTokenExpired();

    this.tokenCheckInterval = setInterval(() => {
      this.checkIsTokenExpired();
    }, 10000);
  };

  private clearTokenExpirationCheck = () => {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }
  };

  async componentDidMount(): Promise<void> {
    // CODE GENERATION FLAGS!!!
    const IS_SAVE_APPLICATION_ENABLED: boolean = true;
    const IS_EXPIRE_OLD_SESSIONS_ENABLED: boolean = false;

    if (this.props.appMode === "WebSite" && IS_SAVE_APPLICATION_ENABLED) {
      await this.handleRequestSaveApplication();
      await ApplicationService.saveAllApplicationsToSessionStorage();
      ApplicationService.injectPreConnectLinks();
    }

    // When mounted as an MFE, redeem whatever auth proof the host gave us on
    // *our* backend before letting the route tree render — see
    // computeMfeHandoffPending. Our own is_logged_in is read per-app
    // (is_logged_in__<apiHost>), so a handoff is pending only when this app's
    // backend has no session yet, regardless of the host's login state.
    if (this.state.mfeHandoffPending) {
      try {
        await this.runMfeAuthHandoff();
      } finally {
        this.setState({ mfeHandoffPending: false });
      }
    }

    this.setKuikaInfo();

    this.setState({ isMounted: true }, () => {
      localStorage.removeItem("isGoogleLoginInProgress");
      localStorage.removeItem("isGoogleLoginAborted");
    });

    // wasm-initialization-placeholder
    // Note: In generated code, this is replaced with actual WASM init

    
    this.sessionService.registerEventListeners();

    if (IS_EXPIRE_OLD_SESSIONS_ENABLED) {
      this.startTokenExpirationCheck();
    }
  }

  componentWillUnmount() {
    this.sessionService.destroyEventListeners();

    this.clearTokenExpirationCheck();
  }

  private runMfeAuthHandoff = async (): Promise<void> => {
    // Path 1 — new-auth host: a handoff JWT was injected as external_access_token.
    // Redeem it on this app's backend to mint our own session cookies.
    const handoffToken = this.props.external_access_token;
    if (handoffToken && isMfeHandoffToken(handoffToken)) {
      try {
        await UserService.LoginWithMfeToken(handoffToken);
      } catch {
        // Redemption failed. Fall through; PrivateRoute will redirect to /login.
      }
      return;
    }

    // Path 2 — pre-cookie-auth host: tokens live in shared localStorage.
    // /auth/loginwithtoken resolves the user from the request's username field,
    // not the JWT signature, so pull UserName out of the legacy token (no
    // signature check needed here — the backend issues a fresh session token).
    const legacyAccessToken = localStorage.getItem("tokenString");
    if (!legacyAccessToken) return;
    const legacyRefreshToken = localStorage.getItem("refreshToken");
    const username = readUsernameFromJwt(legacyAccessToken);
    if (!username) return;

    try {
      await UserService.LoginWithExternalToken({
        providerType: EExternalTokenProviderTypes.None,
        username,
        tokenString: legacyAccessToken,
        refreshToken: legacyRefreshToken ?? ""
      });
    } catch {
      // Best-effort fallback. If the legacy token can't be exchanged, regular
      // PrivateRoute redirects to login as before.
    }
  };

  setKuikaInfo() {
    if (this.props === undefined) {
      return;
    }
    if (!window.kuika) {
      window.kuika = {};
    }
    window.kuika.info = {};
    const appName = "vadenB2B";
    const workspace = "yildizPul";
    const webComponentTag = "yildiz-pul-vaden-b2b";
    const frontEndUrl = KuikaAppManager.GetFrontEndSourceUrl();
    const backEndUrl = KuikaAppManager.GetBackendUrl(true);
    const { authType } = this.props;
    const html = String.raw`
      <html>
        <head>
        <script src='%PUBLIC_URL%/assets/kuika-mfe_1_0_0.js'></script>
        <script>
        CreateKuikaMfeApp({frontEndUrl:'${frontEndUrl}'});
        </script>
        </head>
        <body>
          <yildiz-pul-vaden-b2b></yildiz-pul-vaden-b2b>
        </body>
      </html>`;
    window.kuika.info.workspace = workspace;
    window.kuika.info.applicationName = appName;
    window.kuika.info.webComponentTag = webComponentTag;
    window.kuika.info.frontEndUrl = frontEndUrl;
    window.kuika.info.backEndUrl = backEndUrl;
    window.kuika.info.authType = authType;
    window.kuika.html = html;
    window.kuika.getModule = () => {
      console.log(window.kuika.html);
    };
  }

  handleRequestSaveApplication = async () => {
    const SAVE_APP_REQUEST_KEY = `SAVE_APP_REQUESTED_${KMainFunctions.virtualDirectoryURL}`;

    const isRequestedBefore: string = sessionStorage.getItem(SAVE_APP_REQUEST_KEY);

    if (isRequestedBefore) {
      return;
    }

    try {
      await ApplicationService.saveApplication();
      sessionStorage.setItem(SAVE_APP_REQUEST_KEY, "true");
    } catch (e) {
      setTimeout(this.handleRequestSaveApplication.bind(this), 10000);
    }
  };

  
  onClickContinue = () => {
    this.sessionService.onClickContinue();
  };

  onClickLogout = () => {
    this.sessionService.onClickLogout();
  };


  updateRemainingSessionTime = (remainingSessionTime: number) => {
    this.setState({ remainingSessionTime });
  };

  private getScreenInputProps = (): Record<string, unknown> => {
    const screenInputProps: Record<string, unknown> = {};
    Object.keys(this.props || {}).forEach((key) => {
      if (key.startsWith("screenInput_")) {
        screenInputProps[key] = this.props[key];
      }
    });
    return screenInputProps;
  };

  public render() {
    // Render nothing until the MFE auth handoff completes. The screen tree
    // (PrivateRoute → starting screen → componentDidMount → API calls) runs
    // synchronously on first render, so any delayed redemption would race the
    // first API call and we'd be back to the 401 → refresh 500 → /login bounce.
    if (this.state.mfeHandoffPending) {
      return null;
    }

    return (
      <GoogleOAuthProvider clientId="GOOGLEKEY">
        <KuikaAuthApp
          authType={this.props.authType}
          routerType={this.props.routerType}
          appMode={this.props.appMode}
          external_user={this.props.external_user}
          external_access_token={this.props.external_access_token}
          external_refresh_token={this.props.external_refresh_token}
          startingScreen={this.props.startingScreen}
          {...this.getScreenInputProps()}
        />
        {/* Twin chat: a global floating FAB (bottom-right), mounted once at the app
            root so it persists across screen navigation. Renders nothing unless Twin
            is configured and the user is logged in. */}
        <TwinChatWidget />
        
<Modal
          open={this.logoutWarningThreshold > 0 && this.state.remainingSessionTime <= this.logoutWarningThreshold}
          footer={[
                    <Button danger key="logout" onClick={this.onClickLogout}>{ translationService.translate('common.logout') }</Button>,
                    <Button type="primary" key="continue" onClick={this.onClickContinue}>
                      { translationService.translate('common.continue') }
                    </Button>
                    ]}
        >
          <div>
            { translationService.translate('session_timeout.logout_warning_text', undefined, { seconds: this.state.remainingSessionTime.toString() }) }
          </div>
        </Modal>

      </GoogleOAuthProvider>
    );
  }
}

